import { MutationTree, GetterTree, ActionTree } from "vuex";
import { RootState } from "vuefront-store";
import type {
  AccountCustomer,
  AccountError,
  AccountResult,
  AuthState,
} from "~/core/account/auth";
import {
  ACCOUNT_LOGIN,
  ACCOUNT_LOGOUT,
  ACCOUNT_REGISTER,
  ACCOUNT_REGISTRATION_POLICY,
  ACCOUNT_REQUEST_RESET,
  ACCOUNT_RESET,
  ACCOUNT_RESEND,
  ACCOUNT_SESSION,
  ACCOUNT_VERIFY,
  ACCOUNT_UPDATE_PROFILE,
  ACCOUNT_REQUEST_EMAIL_CHANGE,
  ACCOUNT_CANCEL_EMAIL_CHANGE,
  ACCOUNT_VERIFY_EMAIL_CHANGE,
  ACCOUNT_CHANGE_PASSWORD,
  ACCOUNT_ADDRESSES,
  ACCOUNT_ADDRESS,
  ACCOUNT_CREATE_ADDRESS,
  ACCOUNT_UPDATE_ADDRESS,
  ACCOUNT_DELETE_ADDRESS,
  ACCOUNT_SET_DEFAULT_ADDRESS,
} from "~/core/account/graphql";

export type State = {
  customer: AccountCustomer | null;
  authState: AuthState;
  verificationRequired: boolean;
  error: AccountError | null;
  sessionRequest: number;
  termsVersion: string;
  termsUrl: string;
  registrationPolicyState: "idle" | "checking" | "ready" | "error";
  hadAuthenticatedSession: boolean;
  sessionExpired: boolean;
};
export const state = (): State => ({
  customer: null,
  authState: "checking",
  verificationRequired: false,
  error: null,
  sessionRequest: 0,
  termsVersion: "",
  termsUrl: "/terms",
  registrationPolicyState: "idle",
  hadAuthenticatedSession: false,
  sessionExpired: false,
});
export const getters: GetterTree<State, RootState> = {
  get: (state) => state.customer || {},
  auth: (state) => state.authState === "authenticated",
  authState: (state) => state.authState,
  checking: (state) => state.authState === "checking",
  verificationRequired: (state) => state.verificationRequired,
  error: (state) => state.error,
  registrationPolicy: (state) => ({
    termsVersion: state.termsVersion,
    termsUrl: state.termsUrl,
  }),
  registrationPolicyState: (state) => state.registrationPolicyState,
  sessionExpired: (state) => state.sessionExpired,
};
export const mutations: MutationTree<State> = {
  beginSessionCheck(state) {
    state.sessionRequest += 1;
    state.authState = "checking";
    state.error = null;
  },
  invalidateSessionChecks(state) {
    state.sessionRequest += 1;
  },
  applySession(
    state,
    {
      session,
      requestId,
    }: { session: AccountResult["session"]; requestId?: number }
  ) {
    if (requestId !== undefined && requestId !== state.sessionRequest) return;
    if (requestId === undefined) state.sessionRequest += 1;
    state.authState =
      session.state === "AUTHENTICATED" ? "authenticated" : "anonymous";
    if (state.authState === "authenticated" && session.customer) {
      state.customer = session.customer;
      state.verificationRequired = session.verificationRequired;
      state.hadAuthenticatedSession = true;
      state.sessionExpired = false;
    } else {
      state.sessionExpired = state.hadAuthenticatedSession;
      state.hadAuthenticatedSession = false;
      state.customer = null;
      state.verificationRequired = false;
      state.authState = "anonymous";
    }
    state.error = null;
  },
  setError(state, error: AccountError | null) {
    state.error = error;
  },
  clearPrivate(state, payload: { expired?: boolean } = {}) {
    state.sessionRequest += 1;
    state.customer = null;
    state.verificationRequired = false;
    state.authState = "anonymous";
    state.hadAuthenticatedSession = false;
    state.sessionExpired = payload.expired === true;
    state.error = null;
  },
  acknowledgeSessionExpiry(state) {
    state.sessionExpired = false;
  },
  setRegistrationPolicy(state, policy) {
    state.termsVersion = policy.termsVersion;
    state.termsUrl = policy.termsUrl;
    state.registrationPolicyState = "ready";
  },
  beginRegistrationPolicy(state) {
    state.registrationPolicyState = "checking";
  },
  failRegistrationPolicy(state) {
    state.termsVersion = "";
    state.registrationPolicyState = "error";
  },
};
function transportError(): AccountError {
  return {
    code: "SERVICE_UNAVAILABLE",
    message:
      "We couldn’t reach the account service. Check your connection and try again.",
    fieldErrors: [],
  };
}

const sessionChecks = new WeakMap<object, Promise<any>>();

export const actions: ActionTree<State, RootState> = {
  async checkLogged({ state, commit, dispatch }) {
    const owner = this as object;
    const existing = sessionChecks.get(owner);
    if (existing) return existing;

    const request = (async () => {
      commit("beginSessionCheck");
      const requestId = state.sessionRequest;
      const hadPrivateState = Boolean(state.customer);
      try {
        const { data } = await this.$vfapollo.query({
          query: ACCOUNT_SESSION,
          fetchPolicy: "network-only",
        });
        const session = data.accountSessionV2;
        if (requestId !== state.sessionRequest) return session;
        const previousCustomerId = state.customer?.id || null;
        const nextCustomerId = session.customer?.id || null;
        if (
          session.state !== "AUTHENTICATED" ||
          (previousCustomerId !== null && previousCustomerId !== nextCustomerId)
        ) {
          commit("store/wishlist/clearPrivate", null, { root: true });
          commit("store/order/clearPrivate", null, { root: true });
        }
        commit("applySession", { session, requestId });
        if (
          session.state === "AUTHENTICATED" &&
          typeof window !== "undefined"
        ) {
          await dispatch("store/wishlist/afterAuthentication", null, {
            root: true,
          });
        }
        if (session.state !== "AUTHENTICATED" && hadPrivateState) {
          await this.$vfapollo.clearStore();
        }
        return session;
      } catch {
        if (requestId === state.sessionRequest) {
          commit("store/wishlist/clearPrivate", null, { root: true });
          commit("store/order/clearPrivate", null, { root: true });
          commit("clearPrivate");
          commit("setError", transportError());
        }
        return null;
      } finally {
        if (sessionChecks.get(owner) === request) sessionChecks.delete(owner);
      }
    })();
    sessionChecks.set(owner, request);
    return request;
  },
  async loadRegistrationPolicy({ commit }) {
    commit("beginRegistrationPolicy");
    try {
      const { data } = await this.$vfapollo.query({
        query: ACCOUNT_REGISTRATION_POLICY,
        fetchPolicy: "network-only",
      });
      commit("setRegistrationPolicy", data.accountRegistrationPolicyV2);
      return data.accountRegistrationPolicyV2;
    } catch (error) {
      commit("failRegistrationPolicy");
      throw error;
    }
  },
  async login({ commit, dispatch }, input) {
    let result: AccountResult;
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_LOGIN,
        variables: { input },
      });
      await this.$vfapollo.clearStore();
      result = data.accountLoginV2;
    } catch {
      result = { ok: false, error: transportError() } as AccountResult;
    }
    if (result.session) commit("applySession", { session: result.session });
    if (result.ok && typeof window !== "undefined") {
      await dispatch("store/wishlist/afterAuthentication", null, {
        root: true,
      });
    }
    commit("setError", result.error);
    return result;
  },
  async register({ commit, dispatch }, input) {
    let result: AccountResult;
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_REGISTER,
        variables: { input },
      });
      await this.$vfapollo.clearStore();
      result = data.accountRegisterV2;
    } catch {
      result = { ok: false, error: transportError() } as AccountResult;
    }
    if (result.session) commit("applySession", { session: result.session });
    if (result.ok && typeof window !== "undefined") {
      await dispatch("store/wishlist/afterAuthentication", null, {
        root: true,
      });
    }
    commit("setError", result.error);
    return result;
  },
  async logout({ commit }) {
    commit("invalidateSessionChecks");
    let ok = false;
    try {
      await this.$vfapollo.mutate({ mutation: ACCOUNT_LOGOUT });
      ok = true;
    } catch {
      commit("setError", transportError());
    } finally {
      await this.$vfapollo.clearStore();
      commit("clearPrivate");
      commit("store/wishlist/clearPrivate", null, { root: true });
      commit("store/order/clearPrivate", null, { root: true });
    }
    return { ok };
  },
  async verifyEmail({ commit }, token: string) {
    let result: AccountResult;
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_VERIFY,
        variables: { token },
      });
      await this.$vfapollo.clearStore();
      result = data.accountVerifyEmailV2;
    } catch {
      return { ok: false, error: transportError() };
    }
    if (result.session) commit("applySession", { session: result.session });
    return result;
  },
  async resendVerification() {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_RESEND,
      });
      return data.accountResendVerificationV2;
    } catch {
      return { ok: false, error: transportError() };
    }
  },
  async requestPasswordReset(_context, email: string) {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_REQUEST_RESET,
        variables: { email },
      });
      return data.accountRequestPasswordResetV2;
    } catch {
      return { ok: false, error: transportError() };
    }
  },
  async resetPassword({ commit }, input) {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_RESET,
        variables: { input },
      });
      await this.$vfapollo.clearStore();
      const result = data.accountResetPasswordV2;
      commit("applySession", { session: result.session });
      return result;
    } catch {
      return { ok: false, error: transportError() };
    }
  },
  async updateProfile({ commit }, input) {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_UPDATE_PROFILE,
        variables: { input },
      });
      const result = data.accountUpdateProfileV2;
      if (result.session) commit("applySession", { session: result.session });
      return result;
    } catch {
      return { ok: false, error: transportError() };
    }
  },
  async requestEmailChange({ commit }, email: string) {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_REQUEST_EMAIL_CHANGE,
        variables: { email },
      });
      const result = data.accountRequestEmailChangeV2;
      if (result.session) commit("applySession", { session: result.session });
      return result;
    } catch {
      return { ok: false, error: transportError() };
    }
  },
  async cancelEmailChange({ commit }) {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_CANCEL_EMAIL_CHANGE,
      });
      const result = data.accountCancelEmailChangeV2;
      if (result.session) commit("applySession", { session: result.session });
      return result;
    } catch {
      return { ok: false, error: transportError() };
    }
  },
  async verifyEmailChange({ commit }, token: string) {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_VERIFY_EMAIL_CHANGE,
        variables: { token },
      });
      await this.$vfapollo.clearStore();
      const result = data.accountVerifyEmailChangeV2;
      if (result.session) commit("applySession", { session: result.session });
      return result;
    } catch {
      return { ok: false, error: transportError() };
    }
  },
  async changePassword({ commit }, input) {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_CHANGE_PASSWORD,
        variables: input,
      });
      await this.$vfapollo.clearStore();
      const result = data.accountChangePasswordV2;
      if (result.session) commit("applySession", { session: result.session });
      return result;
    } catch {
      return { ok: false, error: transportError() };
    }
  },
  async loadAddresses() {
    try {
      const { data } = await this.$vfapollo.query({
        query: ACCOUNT_ADDRESSES,
        fetchPolicy: "network-only",
      });
      return { ok: true, addresses: data.accountAddressesV2 };
    } catch {
      return { ok: false, error: transportError(), addresses: [] };
    }
  },
  async loadAddress(_context, id: string) {
    try {
      const { data } = await this.$vfapollo.query({
        query: ACCOUNT_ADDRESS,
        variables: { id },
        fetchPolicy: "network-only",
      });
      return {
        ok: Boolean(data.accountAddressV2),
        address: data.accountAddressV2,
        error: data.accountAddressV2
          ? null
          : {
              code: "ADDRESS_NOT_FOUND",
              message: "Address not found.",
              fieldErrors: [],
            },
      };
    } catch {
      return { ok: false, error: transportError(), address: null };
    }
  },
  async createAddress(_context, input) {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_CREATE_ADDRESS,
        variables: { input },
      });
      return data.accountCreateAddressV2;
    } catch {
      return { ok: false, error: transportError(), addresses: [] };
    }
  },
  async updateAddress(_context, { id, input }) {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_UPDATE_ADDRESS,
        variables: { id, input },
      });
      return data.accountUpdateAddressV2;
    } catch {
      return { ok: false, error: transportError(), addresses: [] };
    }
  },
  async deleteAddress(_context, id: string) {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_DELETE_ADDRESS,
        variables: { id },
      });
      return data.accountDeleteAddressV2;
    } catch {
      return { ok: false, error: transportError(), addresses: [] };
    }
  },
  async setDefaultAddress(_context, id: string) {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_SET_DEFAULT_ADDRESS,
        variables: { id },
      });
      return data.accountSetDefaultAddressV2;
    } catch {
      return { ok: false, error: transportError(), addresses: [] };
    }
  },
};
